import { throttle } from 'lodash'
import type Plivo from 'plivo-browser-sdk'
import type { CallInfo } from 'plivo-browser-sdk/managers/callSession'
import { notify } from 'v2/shared/utils/browser-notification-manager'
import { primaryTabManager } from 'v2/shared/utils/primary-tab-manager'
import { usePhoneStore } from 'v2/state/phone'
import { useTabStore } from 'v2/state/tab'
import { type CallDetails, CallDirection, type CallMeta } from 'v2/types/call'
import { PrimaryEvents, primaryEventBus } from '../../event-bus/primary'
import { phoneLogger } from '../../logger'
import { errorToast, infoToast } from '../../toasts'
import type { CallEventHandlers, IEventManager } from '../types'
import { isAnonymousCallerId } from '../utils/helpers'
import type { ConnectionManager } from './connection-manager'

export class EventManager implements IEventManager {
  sdk: Plivo | null = null

  private connectionManager: ConnectionManager

  constructor(connectionManager: ConnectionManager) {
    this.connectionManager = connectionManager
  }

  setupEventListeners(): void {
    if (!this.sdk?.client?.on) {
      phoneLogger.warn('Cannot setup event listeners - SDK not initialized')
      return
    }

    this.sdk.client.removeAllListeners()
    phoneLogger.info('Setting up Plivo event listeners')

    this.setupLoginEvents()
    this.setupCallEventHandlers()
    this.setupMediaEventHandlers()
    this.setupUnloadHandler()
  }

  private setupLoginEvents(): void {
    this.sdk?.client.on('onLogin', () => {
      phoneLogger.local('Logged in')
      primaryEventBus.emit(PrimaryEvents.SDK_LOGGED_IN)
      usePhoneStore.getState().setRegistered(true)
      useTabStore.getState().setIsPhoneConnected(true)
    })

    this.sdk?.client.on('onWebsocketConnected', () => {
      phoneLogger.info('Connected to WebSocket')
      useTabStore.getState().setIsPhoneConnected(true)
      if (primaryTabManager.getIsPrimary()) {
        const headers = this.connectionManager.getHeaders()
        this.sdk?.client.register(headers)
      }
    })

    this.sdk?.client.on('onLogout', () => {
      phoneLogger.info('Logged out')
      usePhoneStore.getState().reset(true)
    })

    this.sdk?.client.on('onLoginFailed', (reason) => {
      phoneLogger.error('Login failed:', reason)
      usePhoneStore.getState().reset(true)
    })
  }

  private showCallNotAvailableToast(callInfo: CallInfo): void {
    if (callInfo.reason === 'Call completed elsewhere')
      infoToast({
        title: '',
        description: 'Call accepted by different agent',
      })
  }

  private setupCallEventHandlers(): void {
    const handlers: CallEventHandlers = {
      onCallAnswered: (callInfo) => {
        usePhoneStore.getState().setCallActive(true)
        usePhoneStore.getState().setActiveCall({ callUUID: callInfo.callUUID })
        phoneLogger.info(`Call answered with UUID: ${callInfo.callUUID}`)
      },

      onCallFailed: (reason, callInfo) => {
        primaryEventBus.emit(PrimaryEvents.CALL_FAILED, { reason, callInfo })
        phoneLogger.error('Call failed:', reason, callInfo)
        usePhoneStore.getState().reset()

        if (callInfo.code === 1000 || callInfo.code === 3020) return

        errorToast({
          title: 'Call failed',
          description: this.getFailureReason(reason, callInfo),
        })
      },

      onCallTerminated: (hangupInfo, callInfo) => {
        this.showCallNotAvailableToast(callInfo)
        phoneLogger.debug('Call terminated', hangupInfo, callInfo)
        setTimeout(() => {
          primaryEventBus.emit(PrimaryEvents.CALL_TERMINATED)
          primaryEventBus.broadcastToAll(PrimaryEvents.CALL_TERMINATED_BROADCAST)
        }, 500)
      },

      onIncomingCall: (callerID, extraHeaders, callInfo, contactName) => {
        phoneLogger.info('Incoming call received with args', {
          callerID,
          extraHeaders,
          callInfo,
          contactName,
        })

        const callDetails = this.processIncomingCallDetails(
          callerID,
          extraHeaders,
          callInfo,
          contactName,
        )
        notify.incomingCall(callDetails.contactName || callDetails.callWith)
        usePhoneStore.getState().setActiveCall(callDetails)
        primaryEventBus.broadcastToAll(PrimaryEvents.ON_INCOMING_CALL, callDetails)

        this.handleRedirectedCall(callInfo, callDetails)
      },

      onIncomingCallCanceled: (callInfo) => {
        phoneLogger.info('Incoming call was canceled by caller', callInfo)
        usePhoneStore.getState().reset()
        this.showCallNotAvailableToast(callInfo)
      },
    }

    Object.entries(handlers).forEach(([event, handler]) => {
      this.sdk?.client.on(event, handler)
    })
  }

  private setupMediaEventHandlers(): void {
    this.sdk?.client.on('onMediaPermission', (status) => {
      phoneLogger.info('Media permission status:', status)
      if (status.status === 'failure') {
        phoneLogger.error('Media permission error:', status.error)
      }
    })

    this.sdk?.client.on('onMediaConnected', (callInfo) => {
      phoneLogger.info('Media connected for call:', callInfo)
      usePhoneStore.getState().setActiveCall({
        callUUID: callInfo.callUUID,
      })

      const activeCall = usePhoneStore.getState().activeCall
      if (activeCall?.contactName === 'Playground') {
        // Reduce agent playback volume to minimize echo bleed into mic
        document.querySelectorAll('audio').forEach((el) => {
          if (el.srcObject) el.volume = 0.4
        })
        phoneLogger.info('Reduced remote audio volume for playground call')
      }
    })

    this.sdk?.client.on(
      'mediaMetrics',
      throttle((metrics) => {
        if (metrics.level === 'warning') {
          phoneLogger.warn(`Call quality issue: ${metrics.type}`, metrics)
        } else phoneLogger.debug('Call quality metrics:', metrics)
      }, 500),
    )
  }

  private setupUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      if (this.sdk?.client?.getCurrentSession()) {
        phoneLogger.info('Unloading page - ending call and resetting phone state')
        primaryEventBus.broadcast(PrimaryEvents.CALL_TERMINATED, true)
        localStorage.setItem('INVALIDATE_CONVERSATIONS', 'true')
      }
    })
  }

  private getFailureReason(reason: string, callInfo: CallInfo | CallDetails): string {
    if (callInfo.code === 1000 || callInfo.code === 3020)
      return 'Reason: Call rejected by remote party'
    if (callInfo.reason === 'User Denied Media Access')
      return 'Reason: Microphone access denied. Please provide microphone access and try again'
    if (callInfo.reason === 'USER_BUSY')
      return 'The call could not be placed because the user is busy. Please try again later.'
    return `Call failed with reason: ${callInfo.reason || reason}`
  }

  private handleRedirectedCall(
    callInfo: { reason: string },
    callDetails: { callUUID: string },
  ): void {
    if (callInfo.reason === 'redirected') {
      this.sdk?.client?.answer(callDetails.callUUID, '')
    }
  }

  removeAllListeners(): void {
    phoneLogger.info('Removing all Plivo event listeners')
    this.sdk?.client?.removeAllListeners()
    phoneLogger.info('All event listeners removed')
  }

  private processIncomingCallDetails(
    callerID: string,
    extraHeaders: Record<string, string>,
    callInfo: CallInfo & { callerNumber: string },
    contactName: string,
  ) {
    const aLegUUID = extraHeaders['X-Ph-Aleg-Uuid'] || ''
    const aLegToNumber = extraHeaders['X-Ph-Aleg-To-Number'] || ''
    const handoffConversation = decodeURI(extraHeaders['X-Ph-Handoffconv'] || '')
    const autoAcceptDelay = Number(decodeURI(extraHeaders['X-Ph-Autoacceptdelay'] || '0'))
    const autoAccept = decodeURI(extraHeaders['X-Ph-Allowautoaccept'] || '') === 'true'
    const meta = decodeURI(extraHeaders['X-Ph-Header1'] || '') as CallMeta
    const isOnnetCall = extraHeaders['X-Ph-On-Net-Call'] === 'true'

    const callerNumber = callInfo.callerNumber || callerID
    const isAnonymous = callerNumber.startsWith('Anonymous') || isAnonymousCallerId(callerNumber)

    const getCallWith = () => {
      if (isAnonymous) return 'Anonymous'
      return callInfo.direction === 'incoming' ? callInfo.src : callInfo.dest
    }

    return {
      callUUID: aLegUUID || callInfo.callUUID,
      direction: CallDirection.INBOUND,
      code: callInfo.code,
      startTime: new Date(),
      callWith: getCallWith(),
      extraHeaders: {
        meta,
        aliasName: decodeURI(extraHeaders['X-Ph-Aliasname'] || ''),
        teamName: decodeURI(extraHeaders['X-Ph-Teamname'] || ''),
        fromAgent: decodeURI(extraHeaders['X-Ph-Fromagent'] || ''),
      },
      aLegUUID,
      aLegToNumber,
      handoffConversation,
      contactName,
      autoAcceptDelay,
      autoAccept,
      meta,
      isOnnetCall,
      isAnonymous,
    }
  }
}
